import {useEffect} from 'react'
import type {RefObject} from 'react'

/**
 * Closes a popover when the user clicks outside `ref` or presses Escape.
 * Shared by the top-bar dropdowns (AccountMenu, StoreSwitcher) so both dismiss
 * the same way. Listeners are only attached while `active` is true.
 */
export const useClickAway = (
  ref: RefObject<HTMLElement>,
  onAway: () => void,
  active: boolean,
): void => {
  useEffect(() => {
    if (!active) return

    const handlePointer = (event: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(event.target as Node)) {
        onAway()
      }
    }
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onAway()
    }

    document.addEventListener('mousedown', handlePointer)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handlePointer)
      document.removeEventListener('keydown', handleKey)
    }
  }, [ref, onAway, active])
}
